import clsx from "clsx";
import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory, useParams, Link as RouterLink } from "react-router-dom";
import { DateTime } from "luxon";

import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Chip,
  MenuItem,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import { FilledTextField, FilledSelect } from "../../components/FormElements";
import { FleetContext, FleetSettingsContext } from "../../context";
import { useDialog } from "../../hooks/useDialog";
import { StringUtils } from "../../util";

const useStyles = makeStyles((theme) => ({
  infoMessage: { opacity: 0.75 },
  marginRight: { marginRight: theme.spacing(1) },
  dateInput: { paddingRight: theme.spacing(1) },
  timeInput: { paddingLeft: theme.spacing(1) },
  dateTimeInput: {
    "& label": {
      width: "100%",
      "white-space": "nowrap",
      "text-overflow": "ellipsis",
      overflow: "hidden",
    },
  },
  fleetsFieldControl: {
    width: "100%",
    marginTop: theme.spacing(2),
  },
  fleetsField: {
    display: "flex",
    flexWrap: "wrap",
  },
  fleetsFieldItem: {
    background: theme.palette.background.dark,
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  tooltipIcon: { paddingLeft: theme.spacing(0.5) },
  tooltip: { fontSize: "0.75em" },
  fleetCell: { minWidth: "100px" },
  error: {
    padding: 0,
    background: "none",
  },
}));

const SettingsMessagingForm = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();
  const classes = useStyles(theme);

  const { messageId } = useParams();

  const MODES = ["new", "edit"];

  const [open, setOpen] = useState(true);
  const [mode, setMode] = useState(MODES[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dirtyFields, setDirtyFields] = useState([]);

  const { fleets, activeFleet } = useContext(FleetContext);
  const {
    messaging,
    getToastMessage,
    uploadToastMessage,
    setMessagingSettingsForm,
    validateForm,
    invalids,
    schemas,
  } = useContext(FleetSettingsContext);

  const [conflicts, setConflicts] = useState([]);
  const [showConflicts, setShowConflicts] = useState(true);

  const getDateTime = (dateTime) => {
    const date = DateTime.fromISO(dateTime, { zone: activeFleet.timezone });

    const dateStr = date.toLocaleString(DateTime.DATE_MED);
    const timeStr = date.toLocaleString(DateTime.TIME_24_SIMPLE);

    return `${dateStr} ${timeStr}`;
  };

  const getFleetNames = (fleetArray) => fleetArray.map((url) => {
    const value = fleets.find((fleet) => fleet.url === url);
    return value ? value.name : "-";
  }).join(", ");

  const [openSubmitDialog, submitDialog] = useDialog({
    title: t("Fleet.Settings.Messaging.Dialog.title"),
    body: showConflicts
      ? t("Fleet.Settings.Messaging.Dialog.body.conflicts")
      : t("Fleet.Settings.Messaging.Dialog.body.errors"),
    confirmText: showConflicts ? t("Fleet.Settings.Messaging.Dialog.ok") : "",
    children: (
      <Table size="small">
        <TableBody>
          {conflicts.map((conflict) => (
            <TableRow key={conflict.id}>
              <TableCell className={classes.fleetCell}>
                <Grid container alignItems="center">
                  {getFleetNames(conflict.fleets
                    .filter((url) => url === activeFleet.url || messaging.fleets.includes(url)),
                  )}
                  {conflict.fleets.length > 1 &&
                    <Tooltip
                      className={classes.tooltipIcon}
                      classes={{ tooltip: classes.tooltip }}
                      title={t("Fleet.Settings.Messaging.Dialog.warning") +
                        getFleetNames(conflict.fleets)}
                    >
                      <ReportProblemOutlinedIcon fontSize="small" />
                    </Tooltip>
                  }
                </Grid>
              </TableCell>
              <TableCell>{getDateTime(conflict.begin)}</TableCell>
              <TableCell>{getDateTime(conflict.end)}</TableCell>
              <TableCell>
                <Button
                  component={RouterLink}
                  to={{
                    pathname: `/fleets/${StringUtils.uuidFromUrl(conflict.fleets[0])}/settings/messaging`,
                    state: { settingsBackground: location },
                  }}
                  target="_blank"
                >
                  {t("Fleet.Settings.Messaging.Edit")}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    ),
  });

  const allFleets = fleets
    .filter(({ name }) => name !== activeFleet.name)
    .reduce((res, { name, url }) => ({ ...res, [url]: name }), {});

  const [dates, setDates] = useState({ begin: "", end: "" });

  const handleClose = (event) => {
    if (event) {
      event.stopPropagation();
    }

    setOpen(false);
    setMessagingSettingsForm();
    history.goBack();
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");

    const data = await schemas.messaging.cast(messaging);
    const conflictMessages = await uploadToastMessage(
      activeFleet.url,
      { ...data, override: false },
    );

    setLoading(false);

    if (conflictMessages && conflictMessages.length) {
      setConflicts(conflictMessages);
      setShowConflicts(true);

      openSubmitDialog(() => (async () => {
        setLoading(true);

        const conflictErrors = await uploadToastMessage(
          activeFleet.url,
          { ...data, override: true },
        );

        setLoading(false);

        if (conflictErrors && conflictErrors.length) {
          setShowConflicts(false);
          setConflicts(conflictErrors);

          openSubmitDialog(() => (() => {}));
        } else if (conflictErrors && conflictErrors.error) {
          setError(t("Fleet.Settings.Messaging.EditModal.Error"));
        } else {
          handleClose();
        }
      }));
    } else if (conflictMessages && conflictMessages.error) {
      setError(t("Fleet.Settings.Messaging.EditModal.Error"));
    } else {
      handleClose();
    }
  };

  useEffect(() => {
    if (messageId) {
      const loadMessage = async () => {
        await getToastMessage(messageId);
      };
      loadMessage();
    } else if (mode === "new") {
      setMessagingSettingsForm();
      setDates({ begin: "", end: "" });
    }
  }, [messageId]);

  useEffect(() => {
    setMode(MODES.find((m) => location.pathname.includes(m)) || MODES[0]);
  }, [location.pathname]);

  useEffect(() => {
    const timeout = setTimeout(() => { validateForm("messaging"); }, 750);
    return () => clearTimeout(timeout);
  }, [messaging]);

  useEffect(() => {
    setDates({ begin: messaging.begin, end: messaging.end });
  }, [messaging.begin, messaging.end]);

  const handleFormChange = (key, value) => {
    setMessagingSettingsForm({ ...messaging, [key]: value });
  };

  const handleDateChange = (key, type, value) => {
    const date = DateTime.fromISO(messaging[key], { zone: activeFleet.timezone });

    if (type === "time") {
      const [hour, minute] = value.split(":");
      const { year, month, day } = date.toObject();

      const resDate = DateTime.fromObject(
        {
          year,
          month,
          day,
          hour: parseInt(hour, 10),
          minute: parseInt(minute, 10),
        },
        {
          zone: activeFleet.timezone,
        },
      );

      setMessagingSettingsForm({ ...messaging, [key]: resDate.setZone("UTC").toISO() });
    }

    if (type === "date") {
      const [year, month, day] = value.split("-");
      const { hour, minute } = date.toObject();

      const resDate = DateTime.fromObject(
        {
          year: parseInt(year, 10),
          month: parseInt(month, 10),
          day: parseInt(day, 10),
          hour,
          minute,
        },
        {
          zone: activeFleet.timezone,
        },
      );

      setMessagingSettingsForm({ ...messaging, [key]: resDate.setZone("UTC").toISO() });
    }
  };

  const findValidationError = (field) => invalids.messaging.find((err) => err.includes(field));

  const standardTextField = (field, rest) => (
    <FilledTextField
      value={messaging[field]}
      label={t(`Fleet.Settings.Messaging.Table.${field}`)}
      onChange={(e) => handleFormChange(field, e.target.value)}
      onBlur={() => setDirtyFields([...dirtyFields, field])}
      underlineColor={theme.palette.text.lightgrey}
      helperText={dirtyFields.includes(field) ? findValidationError(field) : null}
      error={dirtyFields.includes(field) && !!findValidationError(field)}
      InputProps={{ multiline: field === "message" }}
      disabled={mode === "edit"}
      {...rest}
    />
  );

  const dateTextField = (field, rest) => {
    const { type } = rest;
    const date = DateTime.fromISO(dates[field], { zone: activeFleet.timezone });
    const value = type === "date"
      ? date.toISODate()
      : date.toLocaleString(DateTime.TIME_24_SIMPLE);

    const typeLabel = `${type[0].toUpperCase()}${type.substring(1)}`;
    const label = `${t(`Fleet.Settings.Messaging.Table.${field}${typeLabel}`)}, ${activeFleet.timezone}`;

    return (
      <FilledTextField
        value={dates[field] ? value : ""}
        label={label}
        onChange={(e) => handleDateChange(field, type, e.target.value)}
        onBlur={() => setDirtyFields([...dirtyFields, field])}
        underlineColor={theme.palette.text.lightgrey}
        helperText={dirtyFields.includes(field) ? findValidationError(field) : null}
        error={dirtyFields.includes(field) && !!findValidationError(field)}
        InputProps={{ multiline: field === "text" }}
        {...rest}
      />
    );
  };

  return (
    <Grid>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          {t(`Fleet.Settings.Messaging.EditModal.${mode === "new" ? "CreateMessage" : "EditMessage"}`)}
        </DialogTitle>
        <DialogContent>
          <Grid container>
            {mode === "edit" && messaging.fleets.length > 1 &&
              <Grid container alignItems="center" className={classes.infoMessage}>
                <Tooltip
                  classes={{ tooltip: classes.tooltip }}
                  title={getFleetNames(messaging.fleets)}
                >
                  <InfoOutlinedIcon className={classes.marginRight} />
                </Tooltip>
                {t("Fleet.Settings.Messaging.EditModal.Warning")}
              </Grid>
            }
            <Grid item xs={12}>
              {standardTextField("message", { type: "string", InputLabelProps: { shrink: true } })}
            </Grid>
            <Grid item xs={12}>
              {standardTextField("hyperlink", { type: "string", InputLabelProps: { shrink: true } })}
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={messaging?.dismissable}
                    onChange={(e) => handleFormChange("dismissable", e.target.checked)}
                    value="dismissable"
                    disabled={mode === "edit"}
                  />
                }
                label={t("Fleet.Settings.Messaging.Table.dismissable")}
              />
            </Grid>
            <Grid item xs={6} className={clsx(classes.dateTimeInput, classes.dateInput)}>
              {dateTextField("begin", { type: "date", InputLabelProps: { shrink: true } })}
            </Grid>
            <Grid item xs={6} className={clsx(classes.dateTimeInput, classes.timeInput)}>
              {dateTextField("begin", { type: "time", InputLabelProps: { shrink: true } })}
            </Grid>
            <Grid item xs={6} className={clsx(classes.dateTimeInput, classes.dateInput)}>
              {dateTextField("end", { type: "date", InputLabelProps: { shrink: true } })}
            </Grid>
            <Grid item xs={6} className={clsx(classes.dateTimeInput, classes.timeInput)}>
              {dateTextField("end", { type: "time", InputLabelProps: { shrink: true } })}
            </Grid>
            {mode === "new" &&
              <Grid item xs={12}>
                <FilledSelect
                  label="Copy To"
                  value={messaging.fleets}
                  onChange={(e) => handleFormChange("fleets", e.target.value)}
                  FormControlProps={{
                    className: classes.fleetsFieldControl,
                  }}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) => (
                      <div className={classes.fleetsField}>
                        {selected.map((url) => (
                          <Chip
                            key={url}
                            label={allFleets[url]}
                            className={classes.fleetsFieldItem}
                          />
                        ))}
                      </div>
                    ),
                  }}
                >
                  {Object.keys(allFleets).map((url) => (
                    <MenuItem key={url} value={url}>{allFleets[url]}</MenuItem>
                  ))}
                </FilledSelect>
              </Grid>
            }
          </Grid>
          {loading && <LinearProgress color="secondary" />}
          {error && <Alert severity="error" className={classes.error}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("Fleet.Settings.Messaging.EditModal.Cancel")}</Button>
          <Button onClick={handleSubmit} disabled={invalids.messaging.length > 0}>
            {t("Fleet.Settings.Messaging.EditModal.Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {submitDialog}
    </Grid>
  );
};

export default SettingsMessagingForm;
